import React, { useEffect, useState } from "react";
import {
    ArrowLeft,
    BookOpen,
    ChevronRight,
    HelpCircle,
    Loader2,
    Search,
    Sparkles
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
    getPublicKbHome,
    searchPublicKbArticles
} from "../services/knowledgeBaseService";
import PublicSupportModal from "../components/support/PublicSupportModal";
import "./HelpCenterPage.css";

export default function HelpCenterPage() {
    const navigate = useNavigate();

    const [home, setHome] = useState(null);
    const [articles, setArticles] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [supportModalOpen, setSupportModalOpen] = useState(false);

    useEffect(() => {
        loadHome();
    }, []);

    const loadHome = async () => {
        setLoading(true);

        try {
            const data = await getPublicKbHome();
            setHome(data);
            setArticles(data?.featuredArticles || data?.recentArticles || []);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setSearching(true);

        try {
            const result = await searchPublicKbArticles({
                search,
                page: 1,
                pageSize: 20
            });

            setArticles(result?.items || []);
        } finally {
            setSearching(false);
        }
    };

    return (
        <div className="help-page">
            <header className="help-header">
                <button className="help-back-btn" onClick={() => navigate("/")}>
                    <ArrowLeft size={17} />
                    Back
                </button>

                <div className="help-brand">
                    <Sparkles size={20} />
                    <strong>Shriansh Nexus Help Center</strong>
                </div>

                <button
                    className="help-support-btn"
                    onClick={() => setSupportModalOpen(true)}
                >
                    Contact Support
                </button>
            </header>

            <section className="help-hero">
                <div className="help-pill">
                    <HelpCircle size={16} />
                    Help Center
                </div>

                <h1>How can we help you?</h1>
                <p>
                    Find answers about website generation, previews, publishing, billing,
                    and using Shriansh Nexus.
                </p>

                <form className="help-search-box" onSubmit={handleSearch}>
                    <Search size={18} />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search help articles..."
                    />
                    <button type="submit" disabled={searching}>
                        {searching ? <Loader2 className="spin" size={16} /> : "Search"}
                    </button>
                </form>
            </section>

            {loading ? (
                <div className="help-loading">
                    <Loader2 className="spin" size={26} />
                    Loading help center...
                </div>
            ) : (
                <main className="help-content">
                    <section>
                        <h2>Categories</h2>

                        <div className="help-category-grid">
                            {(home?.categories || []).map((category) => (
                                <div className="help-category-card" key={category.id}>
                                    <BookOpen size={20} />
                                    <h3>{category.name}</h3>
                                    <p>{category.description}</p>
                                    <span>{category.articleCount} articles</span>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section>
                        <h2>{search ? "Search Results" : "Popular Articles"}</h2>

                        {articles.length === 0 ? (
                            <div className="help-empty">
                                <h3>No articles found</h3>
                                <p>Try another search or contact support.</p>
                                <button onClick={() => setSupportModalOpen(true)}>
                                    Contact Support
                                </button>
                            </div>
                        ) : (
                            <div className="help-article-list">
                                {articles.map((article) => (
                                    <button
                                        key={article.id}
                                        className="help-article-card"
                                        onClick={() => navigate(`/help/${article.slug}`)}
                                    >
                                        <div>
                                            <span>{article.categoryName || "Help Article"}</span>
                                            <h3>{article.title}</h3>
                                            <p>{article.summary}</p>
                                        </div>
                                        <ChevronRight size={18} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </section>
                </main>
            )}

            <PublicSupportModal
                open={supportModalOpen}
                onClose={() => setSupportModalOpen(false)}
            />
        </div>
    );
}